import React, {
  useEffect,
  useRef,
  useState,
  useCallback,
  useMemo,
} from 'react';
import {
  Grid,
  Container,
  Typography,
  Box,
  useTheme,
  useMediaQuery,
  Avatar,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Card,
  CardContent,
  Button,
  Stack,
  CircularProgress,
} from '@mui/material';
import Divider from '@mui/material/Divider';
import { useNavigate } from 'react-router-dom';
import { useLocation } from 'react-router-dom';
import { MenuItem } from '@mui/material';
import { MenuList, Paper } from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import ShoppingBasketIcon from '@mui/icons-material/ShoppingBasket';
import EditLocationIcon from '@mui/icons-material/EditLocation';
import PaymentIcon from '@mui/icons-material/Payment';
import LogoutIcon from '@mui/icons-material/Logout';
import EditIcon from '@mui/icons-material/Edit';
import CardGiftcardIcon from '@mui/icons-material/CardGiftcard';
import PersonalInfo from '../../components/Profie/personalInfo';
import OrderList from '../../components/OrderHistory/OrderList';
import Address from '../../components/Address/Address';
import Payment from '../../components/PaymentMethod/Payment';
import footer from '../../../assets/background.png';
import { environment } from '../../../environments/environment';
import { useSelector, useDispatch } from 'react-redux';
import { setUser, UpdateScheduledTime, UpdateCartData } from '../../../../../../libs/redux-slices/src';
import { auth, firestore } from '../../../../../../libs/firebase-service/src';
import { useTranslation } from 'react-i18next';
import SectionHeader from '../../components/common/SectionHeader';
import AddLocationAltIcon from '@mui/icons-material/AddLocationAlt';
import OrderHistoryDetails from '../../components/OrderHistory/OrderHistoryDetails';
import { useOrderActions } from '../../components/OrderHistory/OrderActions';
import NeedQuestion from '../../components/NeedQuestion';
import Rewards from './components/Rewards';
function useQuery() {
  return new URLSearchParams(useLocation().search);
}
const Profile = () => {
  const theme = useTheme();
  const smDown = useMediaQuery(theme.breakpoints.down('sm'));
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const dispatch = useDispatch();
  const token = useSelector((state) => state.authReducer.token);
  const query = useQuery();
  const navigate = useNavigate();
  const [data, setData] = useState([]);
  const [active, setActive] = useState(false);
  const [queryType, setQueryType] = useState(query.get('type'));
  const [lastOrder, setLastOrder] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [visible, setVisible] = useState(false);
  const sentinel = useRef(null);
  const env = useSelector((state) => state.authReducer.env);
  const [cancelOrderget, setCancelOrderget] = useState(false);
  const location = useLocation();
  const licenses = useSelector((state) => state.licenReducer.licenses);
  const [t, i18n] = useTranslation();
  const docid = localStorage.getItem('docid');
  const userData = useSelector((state) => state.authReducer.userData);
  const [selectedOrderForDetails, setSelectedOrderForDetails] = useState(null);
  const [isLoadingOrders, setIsLoadingOrders] = useState(false);

  // Order actions hook
  const { handleReorder, handleCancelOrder, handleRateOrder } =
    useOrderActions();

  // Use refs to access current values without dependency issues
  const dataRef = useRef(data);
  const lastOrderRef = useRef(lastOrder);

  // Update refs when state changes
  useEffect(() => {
    dataRef.current = data;
  }, [data]);

  useEffect(() => {
    lastOrderRef.current = lastOrder;
  }, [lastOrder]);

  // Menu items configuration for FattyPatty design
  const menuItems = useMemo(
    () => [
      {
        id: 'personalInfo',
        label: t('personalInformation') || 'Personal Information',
        icon: PersonIcon,
        component: PersonalInfo,
      },
      {
        id: 'order',
        label: t('orderHistory') || 'Order History',
        icon: ShoppingBasketIcon,
        component: OrderList,
      },
      {
        id: 'address',
        label: t('savedAddresses') || 'Saved Addresses',
        icon: EditLocationIcon,
        component: Address,
      },
      // {
      //   id: 'payment',
      //   label: t('paymentDetails') || 'Payment Details',
      //   icon: PaymentIcon,
      //   component: Payment,
      // },
      {
        id: 'rewards',
        label: t('rewards') || 'Rewards',
        icon: CardGiftcardIcon,
        component: null,
      },
    ],
    [t]
  );
  const checkLicenses = (pageName) => {
    // Use different license structure for fattyPattyDev
    const licenseRoot =
      environment.restaurantId === 'fattyPattyDev'
        ? licenses?.licenses?.frontend?.[' profile_details']
        : environment.restaurantId === 'fattyPatty'
        ? licenses?.licenses?.frontend?.['profile_details']
        : licenses?.licenses?.frontend?.profile_details;
    let result;
    switch (pageName) {
      case 'address':
        result = licenseRoot?.address;
        break;
      case 'account_info':
        result = licenseRoot?.account_info;
        break;
      case 'profile':
        result = licenseRoot?.account_info;
        break;
      case 'payment':
        result = licenseRoot?.payment;
        break;
      case 'orderhistory':
        result = licenseRoot?.orderhistory;
        break;
      case 'rewards':
        result = licenseRoot?.rewards;
        break;
      default:
        result = false;
    }
    return result;
  };

  const checkLicensesdynamic = (pageName) => {
    return licenses?.frontend?.[pageName];
  };

  const handleClick = () => {
    setActive(!active);
  };

  const handleLogout = () => {
    dispatch(UpdateScheduledTime(null));
    dispatch(UpdateCartData({ scheduledTime: null }));
    auth.signOut();
    localStorage.clear();
    if (
      environment?.restaurantId === 'fattyPattyDev' ||
      environment?.restaurantId === 'fattyPatty'
    ) {
      window.location.replace('https://fattypatty.zetaton.dev/');
    } else {
      navigate('/');
    }
  };

  // Helper to get userId with fallback
  const getUserId = useCallback(() => {
    return userData?.uid || docid || auth.currentUser?.uid;
  }, [userData?.uid, docid]);

  // Always refresh user data from Firestore when profile page opens or userId changes
  useEffect(() => {
    const fetchUser = async () => {
      try {
        const userId = getUserId();
        if (!userId) return;

        const userDoc = await firestore.collection('users').doc(userId).get();
        if (userDoc.exists) {
          const freshUserData = { ...userDoc.data(), uid: userId };
          dispatch(setUser({ userData: freshUserData }));
        }
      } catch (error) {
        console.error('Error refreshing user data on profile page:', error);
      }
    };

    fetchUser();
  }, [getUserId, dispatch]);

  // Memoized navigation handler to prevent re-renders
  const handleMenuClick = useCallback(
    (itemId) => {
      const userId = getUserId();
      if (!userId) {
        console.error('Unable to navigate: userId is undefined');
        return;
      }
      navigate(`/profile/${userId}?type=${itemId}`);
      if (isMobile) {
        setVisible(false);
      }
    },
    [navigate, getUserId, isMobile]
  );

  // Memoized helper functions
  const getCurrentTitle = useMemo(() => {
    const currentItem = menuItems.find((item) => item.id === queryType);
    return currentItem?.label || 'Personal Information';
  }, [menuItems, queryType]);

  const formatTitle = useCallback((title) => {
    if (!title) return 'Personal Information';

    const titleMap = {
      order: 'Order History',
      address: 'Saved Addresses',
      personalInfo: 'Personal Information',
      // payment: 'Payment Details',
      rewards: 'Rewards',
    };

    if (titleMap[title]) {
      return titleMap[title];
    }

    return title
      .replace(/([a-z])([A-Z])/g, '$1 $2')
      .replace(/^./, (str) => str.toUpperCase())
      .replace(/\b\w/g, (str) => str.toUpperCase());
  }, []);

  // Memoized load more handler

  const getOrders = useCallback(
    async (isLoadMore = false) => {
      // Set loading state - only show loader for initial load, not for load more
      if (!isLoadMore) {
        setIsLoadingOrders(true);
      }

      let query = firestore
        .collection('orders')
        .where('uid', '==', docid)
        .orderBy('created_at', 'desc')
        .limit(10);

      if (isLoadMore && lastOrderRef.current) {
        query = query.startAfter(lastOrderRef.current);
      }

      try {
        const snapshot = await query.get();

        if (snapshot.size > 0) {
          const array = isLoadMore ? [...dataRef.current] : [];
          let lastdoc = null;
          snapshot.forEach((doc) => {
            array.push(doc.data());
            lastdoc = doc;
          });
          setLastOrder(lastdoc);
          setData(array);
          setHasMore(snapshot.size === 10); // More items if we got a full page
        } else {
          if (!isLoadMore) {
            setData([]);
          }
          setHasMore(false);
        }
      } catch (error) {
        console.error('Error fetching orders:', error);
      } finally {
        if (!isLoadMore) {
          setIsLoadingOrders(false);
        }
      }
    },
    [docid]
  );
  const loadMoreOrders = useCallback(() => {
    getOrders(true);
  }, [getOrders]);
  useEffect(() => {
    if (cancelOrderget) {
      getOrders();
      setCancelOrderget(false);
    }
  }, [cancelOrderget, getOrders]);
  // Memoized scroll handler with throttling
  const handleScroll = useCallback(() => {
    if (
      window.innerHeight + document.documentElement.scrollTop >=
      document.documentElement.offsetHeight - 400
    ) {
      if (hasMore) {
        getOrders(true);
      }
    }
  }, [hasMore, getOrders]);

  useEffect(() => {
    if (queryType === 'order') {
      // Add throttling to prevent excessive scroll events
      let ticking = false;
      const throttledScroll = () => {
        if (!ticking) {
          requestAnimationFrame(() => {
            handleScroll();
            ticking = false;
          });
          ticking = true;
        }
      };

      window.addEventListener('scroll', throttledScroll, { passive: true });
      return () => window.removeEventListener('scroll', throttledScroll);
    }
  }, [queryType, handleScroll]);
  useEffect(() => {
    const currentQueryType = query.get('type') || 'personalInfo';
    setQueryType(currentQueryType);
  }, [query, location]);

  useEffect(() => {
    if (queryType === 'order') {
      getOrders();
    }
  }, [queryType, getOrders]);

  if (
    environment?.restaurantId === 'fattyPattyDev' ||
    environment?.restaurantId === 'fattyPatty'
  ) {
    const renderContent = () => {
      switch (queryType) {
        case 'order':
          if (selectedOrderForDetails) {
            return (
              <OrderHistoryDetails
                order={selectedOrderForDetails}
                onBack={() => setSelectedOrderForDetails(null)}
              />
            );
          }
          return (
            <OrderList
              orders={data}
              isLoading={isLoadingOrders}
              hasMore={hasMore}
              onLoadMore={loadMoreOrders}
              onViewDetails={setSelectedOrderForDetails}
              onReorder={handleReorder}
              onCancelOrder={(orderId) =>
                handleCancelOrder(orderId, () => getOrders())
              }
              onRateOrder={handleRateOrder}
              checkLicenses={checkLicenses}
            />
          );
        case 'address':
          return checkLicenses('address') ? (
            <Address renderAddButton={({ onClick }) => null} />
          ) : null;
        // case 'payment':
        //   return checkLicenses('payment') ? <Payment /> : null;
        case 'rewards':
          return checkLicenses('rewards') ? <Rewards /> : null;
        case 'personalInfo':
        default:
          return checkLicenses('account_info') ? <PersonalInfo /> : null;
      }
    };

    return (
      <Box sx={{ minHeight: '100vh', backgroundColor: '#f8f9fa' }}>
        <SectionHeader
          title={formatTitle(queryType)}
          breadcrumbs={[
            { label: 'Home', path: '/' },
            { label: formatTitle(queryType), current: true },
          ]}
          fontFamily="Jost"
          showBreadcrumbs={true}
          titleVariant="h2"
          customStyles={{
            title: { fontSize: '40px', fontWeight: '700' },
            container: { minHeight: '250px' },
          }}
        />

        {/* Mobile Tab Bar */}
        {isMobile && (
          <Box
            sx={{
              backgroundColor: 'white',
              borderBottom: '1px solid #eee',
              position: 'sticky',
              top: 0,
              zIndex: 100,
            }}
          >
            <Box
              sx={{
                display: 'flex',
                overflowX: 'auto',
                padding: '8px 16px',
                '&::-webkit-scrollbar': { display: 'none' },
              }}
            >
              {menuItems.map((item) => {
                const IconComponent = item.icon;
                const isActive =
                  queryType === item.id ||
                  (!queryType && item.id === 'personalInfo');

                return (
                  <Button
                    key={item.id}
                    onClick={() => handleMenuClick(item.id)}
                    sx={{
                      minWidth: 'auto',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      p: 1.5,
                      mr: 1,
                      borderRadius: 2,
                      backgroundColor: isActive
                        ? 'rgba(255, 107, 53, 0.1)'
                        : 'transparent',
                      color: isActive ? environment.mainColor : '#666',
                      '&:hover': {
                        backgroundColor: isActive
                          ? 'rgba(255, 107, 53, 0.15)'
                          : 'rgba(0,0,0,0.04)',
                      },
                      transition: 'all 0.2s ease',
                    }}
                  >
                    <IconComponent sx={{ fontSize: 20, mb: 0.5 }} />
                    <Typography
                      sx={{
                        fontSize: '0.75rem',
                        fontFamily: 'Jost, sans-serif',
                        fontWeight: isActive ? 600 : 400,
                        textTransform: 'none',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {item?.id == 'order'? 'Orders' : formatTitle(item.id).split(' ')[0]}{' '}
                      {/* Show first word only */}
                    </Typography>
                  </Button>
                );
              })}
              <Button
                onClick={handleLogout}
                sx={{
                  minWidth: 'auto',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  p: 1.5,
                  borderRadius: 2,
                  color: '#ff4757',
                  '&:hover': {
                    backgroundColor: 'rgba(255, 71, 87, 0.08)',
                  },
                }}
              >
                <LogoutIcon sx={{ fontSize: 20, mb: 0.5 }} />
                <Typography
                  sx={{
                    fontSize: '0.75rem',
                    fontFamily: 'Jost, sans-serif',
                    textTransform: 'none',
                  }}
                >
                  Logout
                </Typography>
              </Button>
            </Box>
          </Box>
        )}

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : '280px 1fr',
            gap: isMobile ? 0 : 3,
            maxWidth: '1400px',
            margin: '0 auto',
            padding: { xs: 0, md: 3 },
            minHeight: 'calc(100vh - 300px)',
          }}
        >
          {/* Desktop Sidebar */}
          {!isMobile && (
            <Box
              sx={{
                position: 'sticky',
                top: 20,
                height: 'fit-content',
                maxHeight: 'calc(100vh - 40px)',
              }}
            >
              <Box
                sx={{
                  backgroundColor: 'white',
                  borderRadius: 2,
                  overflow: 'hidden',
                  boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                {/* User Card */}
                <Card sx={{ border: 'none', boxShadow: 'none' }}>
                  <CardContent
                    sx={{
                      textAlign: 'center',
                      py: 4,
                      backgroundColor: environment.mainColor,
                      color: 'white',
                      position: 'relative',
                    }}
                  >
                    <Box sx={{ position: 'relative', display: 'inline-block' }}>
                      <Avatar
                        sx={{
                          width: 80,
                          height: 80,
                          mx: 'auto',
                          mb: 2,
                          border: '4px solid white',
                          fontSize: '2rem',
                          fontWeight: 'bold',
                          backgroundColor: '#ff8c69',
                        }}
                        src={userData?.photoURL}
                        alt={userData?.displayName || 'User'}
                      >
                        {(
                          userData?.displayName ||
                          userData?.fullName ||
                          userData?.email ||
                          'U'
                        )
                          .charAt(0)
                          .toUpperCase()}
                      </Avatar>
                      {/* <Box
                        sx={{
                          position: 'absolute',
                          bottom: 8,
                          right: -8,
                          backgroundColor: '#ff4757',
                          borderRadius: '50%',
                          width: 32,
                          height: 32,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          border: '2px solid white',
                          cursor: 'pointer',
                          transition: 'transform 0.2s',
                          '&:hover': {
                            transform: 'scale(1.1)',
                          },
                        }}
                      >
                        <EditIcon sx={{ fontSize: 16, color: 'white' }} />
                      </Box> */}
                    </Box>
                    <Typography
                      variant="h6"
                      sx={{
                        fontFamily: 'Jost, sans-serif',
                        fontWeight: 600,
                        fontSize: '1.25rem',
                      }}
                    >
                      {userData?.displayName ||
                        userData?.fullName ||
                        userData?.email?.split('@')[0] ||
                        'John Doa'}
                    </Typography>
                  </CardContent>
                </Card>

                {/* Menu List */}
                <Box sx={{ flex: 1, overflow: 'auto' }}>
                  <List sx={{ padding: 0 }}>
                    {menuItems.map((item) => {
                      const IconComponent = item.icon;
                      const isActive =
                        queryType === item.id ||
                        (!queryType && item.id === 'personalInfo');

                      return (
                        <ListItem
                          key={item.id}
                          sx={{
                            cursor: 'pointer',
                            borderLeft: isActive
                              ? `4px solid ${environment.mainColor}`
                              : '4px solid transparent',
                            backgroundColor: isActive
                              ? 'rgba(255, 107, 53, 0.08)'
                              : 'transparent',
                            '&:hover': {
                              backgroundColor: isActive
                                ? 'rgba(255, 107, 53, 0.12)'
                                : 'rgba(0,0,0,0.04)',
                            },
                            transition: 'all 0.3s ease',
                            py: 2,
                            px: 3,
                          }}
                          onClick={() => handleMenuClick(item.id)}
                        >
                          <ListItemIcon
                            sx={{
                              color: isActive ? environment.mainColor : '#666',
                              minWidth: 40,
                            }}
                          >
                            <IconComponent />
                          </ListItemIcon>
                          <ListItemText
                            primary={formatTitle(item.id)}
                            primaryTypographyProps={{
                              fontFamily: 'Jost, sans-serif',
                              fontWeight: isActive ? 600 : 400,
                              color: isActive ? environment.mainColor : '#333',
                              fontSize: '1rem',
                            }}
                          />
                        </ListItem>
                      );
                    })}

                    <ListItem
                      sx={{
                        cursor: 'pointer',
                        '&:hover': {
                          backgroundColor: 'rgba(255, 71, 87, 0.08)',
                        },
                        transition: 'all 0.3s ease',
                        py: 2,
                        px: 3,
                        mt: 2,
                        borderTop: '1px solid #eee',
                      }}
                      onClick={handleLogout}
                    >
                      <ListItemIcon sx={{ color: '#ff4757', minWidth: 40 }}>
                        <LogoutIcon />
                      </ListItemIcon>
                      <ListItemText
                        primary={t('logout') || 'Logout'}
                        primaryTypographyProps={{
                          fontFamily: 'Jost, sans-serif',
                          color: '#ff4757',
                          fontWeight: 500,
                          fontSize: '1rem',
                        }}
                      />
                    </ListItem>
                  </List>
                </Box>
              </Box>
            </Box>
          )}

          {/* Content Area */}
          <Box sx={{ overflow: 'hidden' }}>
            <Card
              sx={{
                borderRadius: { xs: 0, md: 2 },
                boxShadow: { xs: 'none', md: '0 4px 20px rgba(0,0,0,0.1)' },
                height: 'fit-content',
                minHeight: { xs: 'calc(100vh - 200px)', md: '600px' },
              }}
            >
              <CardContent sx={{ p: { xs: 2, md: 4 } }}>
                <Box sx={{ mb: 3 }}>
                  {/* Dynamic header button logic */}
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                  >
                    <Typography
                      variant="h4"
                      sx={{
                        fontFamily: 'Jost, sans-serif',
                        fontWeight: 600,
                        color: '#000000',
                        mb: 1,
                        fontSize: { xs: '20px', md: '24px' },
                        textTransform: 'capitalize',
                      }}
                    >
                      {formatTitle(queryType)}
                    </Typography>
                    {/* Render Add Address button only on address tab */}
                    {queryType === 'address' && (
                      <Button
                        variant="contained"
                        color="primary"
                        sx={{
                          fontFamily: 'Jost, sans-serif',
                          borderRadius: '56px',
                          boxShadow: 'none',
                          padding: '14px 28px',
                          textTransform: 'capitalize',
                          ml: 2,
                          height: '40px',
                          backgroundColor: environment.mainColor,
                          '&:hover': {
                            backgroundColor: `${environment.mainColor} !important`,
                            boxShadow: 'none',
                          },
                        }}
                        onClick={() => {
                          // Find the Address component instance and trigger handleClickOpen
                          // Instead, use a ref or global event, but for now, fallback to window event
                          const event = new CustomEvent('openAddAddressDialog');
                          window.dispatchEvent(event);
                        }}
                      >
                        {t('profilee.addNewAddress')}
                      </Button>
                    )}
                  </Stack>

                  <Typography
                    variant="body1"
                    sx={{
                      fontFamily: 'Jost, sans-serif',
                      color: '#666',
                      fontSize: { xs: '1rem', md: '1.1rem' },
                    }}
                  >
                    Manage Your Data
                  </Typography>
                </Box>

                <Box
                  sx={{
                    maxHeight: { xs: 'none', md: 'calc(100vh - 350px)' },
                    overflow: 'auto',
                    '&::-webkit-scrollbar': {
                      width: '8px',
                    },
                    '&::-webkit-scrollbar-track': {
                      backgroundColor: '#f1f1f1',
                      borderRadius: '4px',
                    },
                    '&::-webkit-scrollbar-thumb': {
                      backgroundColor: '#ff6b35',
                      borderRadius: '4px',
                      '&:hover': {
                        backgroundColor: '#ff5722',
                      },
                    },
                  }}
                >
                  {renderContent()}
                </Box>
              </CardContent>
            </Card>
          </Box>
        </Box>
        <NeedQuestion />
      </Box>
    );
  }

  return (
    <>
      {(environment?.restaurantId === 'fattyPattyDev' ||
        environment?.restaurantId === 'fattyPatty') && (
        <SectionHeader
          title={formatTitle(queryType)}
          breadcrumbs={[
            { label: 'Home', path: '/' },
            { label: formatTitle(queryType), current: true },
          ]}
          fontFamily="Jost"
          showBreadcrumbs={true}
          titleVariant="h2"
          customStyles={{
            title: { fontSize: '40px', fontWeight: '700' },
            container: { minHeight: '250px' },
          }}
        />
      )}
      <div
        style={{
          marginTop:
            environment?.restaurantId != 'elmbrookschools' &&
            environment?.restaurantId != 'alyousef' &&
            environment?.restaurantId != 'filfil' &&
            environment?.restaurantId != 'fattyPattyDev' &&
            environment?.restaurantId != 'fattyPatty' &&
            '80px',
        }}
      >
        {/* <Typography variant="h2">My Profile</Typography>
      <Divider style={{ marginBottom: '60px' }} /> */}
        <Box
          sx={{
            zIndex: 0,
            display: { xs: 'grid', md: 'flex', lg: 'flex' },
            justifyContent: 'space-between',
          }}
        >
          <Grid
            item
            xs={12}
            sm={12}
            lg={2}
            style={{
              background:
                environment?.restaurantId === 'elmbrookschools' ||
                environment?.restaurantId === 'filfil' ||
                environment?.restaurantId === 'alyousef'
                  ? environment.mainColor
                  : '',
            }}
          >
            {' '}
            <MenuList
              style={{
                padding: '40px 1.875rem',
                background: environment.mainColor,
                paddingTop: '90px',
                height: '100%',
              }}
            >
              <MenuItem
                onClick={() => {
                  const userId = getUserId();
                  if (userId) navigate(`/profile/${userId}?type=personalInfo`);
                }}
                style={{
                  marginTop: '15px',
                  marginBottom: '45px',
                }}
              >
                <Typography
                  component="p"
                  variant="h6"
                  style={{
                    color:
                      queryType === 'personalInfo' ||
                      (!queryType && location.pathname.includes('/profile'))
                        ? environment.primaryColor
                        : 'white',
                     textShadow: '0 0 5px rgba(0, 0, 0, 0.3)'
                            
                  }}
                >
                  <PersonIcon
                    style={{
                      color:
                        queryType === 'personalInfo' ||
                        (!queryType && location.pathname.includes('/profile'))
                          ? environment.primaryColor
                          : 'white',
                    }}
                  />{' '}
                  {t('account')}
                </Typography>
              </MenuItem>
              <MenuItem
                onClick={() => {
                  const userId = getUserId();
                  if (userId) navigate(`/profile/${userId}?type=order`);
                }}
                style={{
                  marginTop: '15px',
                  marginBottom: '45px',
                }}
              >
                <Typography
                  component="p"
                  variant="h6"
                  style={{
                    color:
                      queryType === 'order'
                        ? environment.primaryColor
                        : 'white',
                  }}
                >
                  <ShoppingBasketIcon
                    style={{
                      color:
                        queryType === 'order'
                          ? environment.primaryColor
                          : 'white',
                    }}
                  />{' '}
                  {t('ordersHistory')}
                </Typography>
              </MenuItem>
              <MenuItem
                onClick={() => {
                  const userId = getUserId();
                  if (userId) navigate(`/profile/${userId}?type=address`);
                }}
                style={{
                  marginTop: '15px',
                  marginBottom: '45px',
                }}
              >
                <Typography
                  component="p"
                  variant="h6"
                  style={{
                    color:
                      queryType === 'address'
                        ? environment.primaryColor
                        : 'white',
                  }}
                >
                  <EditLocationIcon
                    style={{
                      color:
                        queryType === 'address'
                          ? environment.primaryColor
                          : 'white',
                    }}
                  />{' '}
                  {t('savedAddresses')}
                </Typography>
              </MenuItem>
              {checkLicensesdynamic('payment') && (
                <MenuItem
                  onClick={() => {
                    const userId = getUserId();
                    if (userId) navigate(`/profile/${userId}?type=payment`);
                  }}
                  style={{
                    marginTop: '15px',
                    marginBottom: '15px',
                  }}
                >
                  <Typography
                    component="p"
                    variant="h6"
                    style={{
                      color:
                        queryType === 'payment'
                          ? environment.primaryColor
                          : 'white',
                    }}
                  >
                    <PaymentIcon
                      style={{
                        color:
                          queryType === 'payment'
                            ? environment.primaryColor
                            : 'white',
                      }}
                    />{' '}
                    {t('paymentMethods')}
                  </Typography>
                </MenuItem>
              )}
            </MenuList>
          </Grid>
          <Grid
            item
            xs={12}
            sm={12}
            lg={2}
            style={{
              maxWidth: !smDown && '75%',
              flexBasis: '75%',
              marginTop: smDown ? 20 : '60px',
              paddingLeft: '20px',
            }}
          >
            <div
              style={{
                position: 'relative',
                backgroundImage: `url(${footer})`,
              }}
            >
              <Typography
                component="h2"
                variant="h4"
                style={{
                  fontWeight: 'bold',
                  marginRight: '10px',
                  paddingTop: '60px',
                }}
              >
                {queryType === 'personalInfo'
                  ? `${t('editProfile')}`
                  : queryType === 'order'
                  ? `${t('ordersHistory')}`
                  : queryType === 'address'
                  ? `${t('savedAddresses')}`
                  : queryType === 'payment'
                  ? `${t('paymentMethods')}`
                  : formatTitle(queryType) || `${t('editProfile')}`}
              </Typography>
              <Divider />

              {/* <div>
                <Typography component="p" variant="h6">
                  {queryType === 'order' ? (
                    <>
                      {data.length > 0 ? (
                        data.map((order, index) => (
                          <>
                            {checkLicenses('orderhistory') && (
                              <>
                                <OrderHistory
                                  order={order}
                                  setCancelOrderget={setCancelOrderget}
                                />
                                <div ref={sentinel} />
                              </>
                            )}
                          </>
                        ))
                      ) : (
                        <Typography
                          style={{
                            marginTop: '20px',
                            display: 'flex',
                            justifyContent: 'center',
                          }}
                        >
                          {t('noOrder')}
                        </Typography>
                      )}
                    </>
                  ) : queryType === 'address' ? (
                    <>{checkLicenses('address') && <Address />}</>
                  ) : queryType === 'personalInfo' || !queryType ? (
                    <>
                      {checkLicenses('account_info') ? (
                        <>
                          <PersonalInfo />
                        </>
                      ) : (
                        <>
                          <Typography>Account info not licensed</Typography>
                        </>
                      )}
                    </>
                  ) : queryType === 'payment' ? (
                    <> {checkLicenses('payment') && <Payment />}</>
                  ) : (
                    <>
                      {checkLicenses('account_info') ? (
                        <>
                          <PersonalInfo />
                        </>
                      ) : (
                        <>
                          <Typography>
                            Account info not licensed (fallback)
                          </Typography>
                        </>
                      )}
                    </>
                  )}
                </Typography>
              </div> */}
            </div>
          </Grid>
        </Box>
      </div>
    </>
  );
};
export default Profile;
